import { useEffect, useRef, useState } from "react";
import { Upload, PenLine, Eraser } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { emit } from "./cmd";
import { SIG_PLACEHOLDER, INITIALS_PLACEHOLDER, esc } from "../../lib/pages";
import { listSignatures, saveSignature, deleteSignature } from "../../lib/storage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { DrawPad, TypePad, typedToPng, SIG_FONTS, SavedList } from "./SignaturePads";

const readFile = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

export const SignatureDialog = ({ target, onClose }) => {
  const [img, setImg] = useState("");
  const [name, setName] = useState("");
  const [withDate, setWithDate] = useState(true);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();
  const [tab, setTab] = useState("draw");
  const [drawn, setDrawn] = useState("");
  const [typed, setTyped] = useState({ text: "", font: SIG_FONTS[0], color: "#0F172A" });
  const [saved, setSaved] = useState([]);
  const [saveIt, setSaveIt] = useState(true);
  const [applyAll, setApplyAll] = useState(true);
  const isInitials = target?.dataset.kind === "initials";
  const word = isInitials ? "Initials" : "Signature";
  const others = target ? [...(target.closest(".editor-body")?.querySelectorAll('.sig-field[data-kind="initials"]:not([data-signed="1"])') || [])].filter((f) => f !== target) : [];

  useEffect(() => {
    if (!target) return;
    setImg(target.querySelector("img")?.src || "");
    setName(target.dataset.signer || target.dataset.assignee || "");
    setDrawn("");
    setTyped((t) => ({ ...t, text: target.dataset.signer || target.dataset.assignee || "" }));
    listSignatures().then((all) => {
      setSaved([...all].sort((a, b) => ((b.kind === "initials") === (target.dataset.kind === "initials")) - ((a.kind === "initials") === (target.dataset.kind === "initials"))));
      const mine = all.filter((s) => (s.kind === "initials") === (target.dataset.kind === "initials"));
      setTab(target.querySelector("img") ? "upload" : mine.length ? "saved" : "draw");
    });
  }, [target]);

  const take = async (f) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(f.type)) return toast.error("Please choose a PNG, JPG, WEBP or GIF image");
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4 MB");
    setImg(await readFile(f));
  };

  const keepUi = (html) => {
    const ui = target.querySelector(":scope > .ov-ui");
    target.innerHTML = html;
    if (ui) target.prepend(ui);
  };
  const ready = tab === "draw" ? !!drawn : tab === "type" ? !!typed.text.trim() : !!img;
  const fill = (el, src, signer) => {
    const date = withDate ? new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
    const cap = [signer, date].filter(Boolean).join(" · ");
    const ui = el.querySelector(":scope > .ov-ui");
    el.innerHTML = `<img class="sig-img" src="${src}" alt="${word}">${cap ? `<span class="sig-caption">${esc(cap)}</span>` : ""}`;
    if (ui) el.prepend(ui);
    el.dataset.signed = "1";
    el.dataset.signer = signer;
  };
  const applySrc = (src, signer, fromSaved) => {
    fill(target, src, signer);
    const n = isInitials && applyAll ? others.length : 0;
    if (n) others.forEach((f) => fill(f, src, signer));
    emit();
    if (saveIt && !fromSaved) saveSignature(src, isInitials ? "initials" : "signature", signer);
    toast.success(n ? `${word} applied to ${n + 1} fields` : `${word} applied`);
    onClose();
  };
  const apply = async () => {
    const src = tab === "draw" ? drawn : tab === "type" ? await typedToPng(typed.text.trim(), typed.font, typed.color) : img;
    applySrc(src, name || (tab === "type" ? typed.text.trim() : ""), false);
  };
  const clear = () => {
    keepUi(isInitials ? INITIALS_PLACEHOLDER : SIG_PLACEHOLDER);
    delete target.dataset.signed;
    delete target.dataset.signer;
    emit();
    onClose();
  };
  const signed = target?.dataset.signed === "1";

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="signature-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="h-5 w-5 text-primary" />{isInitials ? "Initial this field" : "Sign this field"}</DialogTitle>
          <DialogDescription>Draw it with your mouse or finger, type it in a handwriting font, or upload an image. It is placed inside the field and stays in this browser.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="saved" data-testid="signature-tab-saved">Saved ({saved.length})</TabsTrigger>
            <TabsTrigger value="draw" data-testid="signature-tab-draw">Draw</TabsTrigger>
            <TabsTrigger value="type" data-testid="signature-tab-type">Type</TabsTrigger>
            <TabsTrigger value="upload" data-testid="signature-tab-upload">Upload</TabsTrigger>
          </TabsList>
          <TabsContent value="saved">
            <SavedList items={saved} onPick={(s) => applySrc(s.src, name || s.label || "", true)}
              onDelete={async (s) => { await deleteSignature(s.id); setSaved((a) => a.filter((x) => x.id !== s.id)); toast("Saved signature removed"); }} />
          </TabsContent>
          <TabsContent value="draw"><DrawPad onChange={setDrawn} /></TabsContent>
          <TabsContent value="type"><TypePad value={typed} onChange={setTyped} /></TabsContent>
          <TabsContent value="upload">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current.click()}
          className={`relative h-40 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors bg-white ${drag ? "border-primary bg-primary/5" : "border-slate-300 hover:border-primary"}`}
          data-testid="signature-dropzone">
          {img ? (
            <div className="flex flex-col items-center w-3/4">
              <img src={img} alt="Signature preview" className="max-h-24 object-contain" data-testid="signature-preview" />
              <div className="w-full border-t border-slate-500 mt-1" />
            </div>
          ) : (
            <div className="text-center text-slate-500 text-sm"><Upload className="h-6 w-6 mx-auto mb-2 text-primary" />Click to upload or drop a signature image<div className="text-xs mt-1">PNG, JPG, WEBP · max 4 MB</div></div>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => { take(e.target.files[0]); e.target.value = ""; }} data-testid="signature-file-input" />
        </div>
          </TabsContent>
        </Tabs>
        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <div className="space-y-1"><Label>Signer name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Doe" data-testid="signature-name-input" /></div>
          <label className="flex items-center gap-2 text-sm h-10"><Checkbox checked={withDate} onCheckedChange={(v) => setWithDate(!!v)} data-testid="signature-date-checkbox" />Add date</label>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {tab !== "saved" && <label className="flex items-center gap-2 text-sm"><Checkbox checked={saveIt} onCheckedChange={(v) => setSaveIt(!!v)} data-testid="signature-save-checkbox" />Save for reuse in other documents</label>}
          {isInitials && others.length > 0 && <label className="flex items-center gap-2 text-sm"><Checkbox checked={applyAll} onCheckedChange={(v) => setApplyAll(!!v)} data-testid="initials-apply-all-checkbox" />Also fill the other {others.length} empty initials field{others.length > 1 ? "s" : ""}</label>}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div>{signed && <Button variant="ghost" onClick={clear} className="text-destructive" data-testid="signature-clear-btn"><Eraser className="h-4 w-4 mr-1.5" />Clear {word.toLowerCase()}</Button>}</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={apply} disabled={!ready || tab === "saved"} data-testid="signature-apply-btn">Apply {word.toLowerCase()}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
